// ============================================================================
// JARVISTRAVEL - RESPONSIVE IMAGE
// One primitive for the app's photography: a responsive srcSet (crisp on large /
// retina screens, small on phones — utils/responsiveImage), lazy-loading for
// off-screen cards, eager + async-decoded for above-the-fold heroes, and a gentle
// fade-in so images settle in instead of popping. Falls back to a plain src for
// non-CDN URLs. className passes through so callers keep their object-fit/hover.
// ============================================================================

import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react';
import { imageSrcSet } from '@/app/utils/responsiveImage';

interface ResponsiveImageProps extends Pick<ImgHTMLAttributes<HTMLImageElement>, 'className'> {
  /** Empty, absent or failing to load renders the brand gradient instead of a
   *  broken image. */
  src?: string;
  alt: string;
  /** Rendered-width hint per breakpoint (see IMG_SIZES). */
  sizes: string;
  /** Above-the-fold hero — load eagerly (it's the LCP); everything else lazy-loads. */
  priority?: boolean;
  /** Told which src failed to load, if any, so the caller can hide what belongs
   *  to the photo, such as its credit (usePhotoCredit). */
  onFailedChange?: (failedSrc: string | undefined) => void;
}

export function ResponsiveImage({ src, alt, sizes, className = '', priority = false, onFailedChange }: ResponsiveImageProps) {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);
  // The src that failed, so a new src gets its own try.
  const [failed, setFailed] = useState<string>();

  // Reported on mount too: a photo that failed before gets a fresh try when it's
  // shown again, so its credit has to come back with it.
  useEffect(() => {
    onFailedChange?.(failed);
  }, [failed, onFailedChange]);

  // Keep the fade-in in sync with `src`: a cached image is already `complete`
  // before onLoad fires (reveal it), and a swapped `src` fades in afresh.
  useEffect(() => {
    setLoaded(ref.current?.complete ?? false);
  }, [src]);

  // React 18 warns on (and would rather we not use) the camelCase `fetchPriority`
  // prop; the lowercase DOM attribute passes straight through, so set it directly
  // to flag the LCP hero for the preload scanner without flooding the console.
  const priorityAttr = priority ? ({ fetchpriority: 'high' } as ImgHTMLAttributes<HTMLImageElement>) : undefined;

  // No image (e.g. Explore activities scraped without one), or one that failed
  // to load (a hotel that moved its photo turns our stay-photo copy into a 404,
  // JAR-2254) — a branded gradient stands in rather than a broken-image icon.
  // className carries the slot's sizing (w/h/rounding); object-fit is a harmless
  // no-op on a div.
  if (!src || src === failed) {
    return <div className={`${className} bg-gradient-to-br from-brand/80 to-accent/70`} role="img" aria-label={alt} />;
  }

  return (
    <img
      ref={ref}
      src={src}
      srcSet={imageSrcSet(src)}
      sizes={sizes}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      // Unsplash's CDN is a third party, and our page addresses can carry a
      // trip id. `no-referrer` sends no Referer header at all — not the path,
      // not even the bare domain.
      //
      // This was `origin` until JAR-1152. Unsplash was asked directly, in a
      // CCPA/GDPR frame, whether no-referrer would interfere with attribution;
      // they answered that it would be fine, subject to still crediting
      // Unsplash and the photographer, which PhotoCredit does and
      // unsplashAttribution.test.tsx enforces. Their view attribution rides on
      // the URL's ixid, not on the Referer, so this costs them nothing.
      //
      // Deliberate, not a default: anyone "simplifying" this to the browser
      // default is undoing a privacy decision that has the vendor's agreement.
      referrerPolicy="no-referrer"
      {...priorityAttr}
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(src)}
      className={`${className} transition-opacity duration-700 motion-reduce:transition-none ${loaded ? 'opacity-100' : 'opacity-0'}`}
    />
  );
}
